import styled, {keyframes} from "styled-components";
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { useSeller } from "../../../context/seller";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import * as CommonTypes from '../../../types/common.types'
import SellerService from "../../../services/seller.service";
import InputDropdown from "../../common/InputDropdown";
import Calendar from "../../common/CalendarInput";
import { format } from "date-fns";
import GeneralInput from "../../common/GeneralInput";
import dollar from '../../../assets/common/dollar.svg'
import Dropdown from "../../common/Dropdown";
import { FaTrash } from "react-icons/fa";
import insert from "../../../utils/insert";
import { useGlobal } from "../../../context/global";
import ToursService from "../../../services/tours.service";
import repartir from "../../../utils/repartir";
import formatCLP from "../../../utils/formatCLP";

export default function CreateReservation () {
    const { setIsLoading } = useGlobal()
    const {createReservationTourId, setCreateReservationTourId, currentDay, user} = useSeller()
    const [newReservation, setNewReservation] = useState<CommonTypes.CreateReservation>({
        tour_id: createReservationTourId,
        reservation_date: currentDay as Date
    } as CommonTypes.CreateReservation)
    const [passengers, setPassengers] = useState<CommonTypes.Passenger[]>([])
    const [joinOptions, setJoinOptions] = useState<CommonTypes.JoinOption[]>([])
    // 'none' | 'g:<group_id>' | 'r:<reservation_id>'
    const [joinChoice, setJoinChoice] = useState<string>('none')
    const reservationDay = format(newReservation.reservation_date, 'yyyy-MM-dd')
    const [tour, setTour] = useState<CommonTypes.TourInfo>()

    useEffect(() => {
        ToursService.getDetailTourInfo(createReservationTourId, user?.user.agency_id || '')
            .then(response => setTour(response.data))
            .catch(error => console.log(error))
    }, [createReservationTourId, user?.user.agency_id])

    // Tour privado (por vehículo): el "+" siempre arma un privado nuevo para este pasajero, que paga
    // el vehículo completo. Un grupo que ya tiene este tour este día es otro vehículo: si se sumara
    // ahí, el backend repartiría el precio con esos pasajeros. Por eso no se ofrece.
    const privado = tour?.unidad_precio === 'por_vehiculo'
    const opciones = useMemo(
        () => joinOptions.filter(o => !privado || !o.tours.some(t => t.tour_id === createReservationTourId && t.date === reservationDay)),
        [joinOptions, privado, createReservationTourId, reservationDay]
    )

    useEffect(() => {
        if (joinChoice !== 'none' && !opciones.some(o => joinKey(o) === joinChoice)) setJoinChoice(opciones.length > 0 ? joinKey(opciones[0]) : 'none')
    }, [opciones, joinChoice])

    // Pregunta 2: los demás del grupo elegido que todavía no tienen este tour este día. Se les crea la
    // misma reserva de una vez (ej. Giuli y Claudia, que ya tenían Piedras juntas, suman Valle).
    const [extras, setExtras] = useState<string[]>([])
    const grupoElegido = opciones.find(o => joinKey(o) === joinChoice)
    const candidatos = useMemo(() => (grupoElegido?.members || []).filter(m =>
        m.id !== newReservation.passenger_id &&
        !m.tours.some(t => t.tour_id === createReservationTourId && t.date === reservationDay)
    ), [grupoElegido, newReservation.passenger_id, createReservationTourId, reservationDay])
    useEffect(() => { setExtras([]) }, [joinChoice, newReservation.passenger_id, reservationDay])

    const pasajero = passengers.find(p => p.id === newReservation.passenger_id)
    const quienes = [
        ...(pasajero ? [pasajero.name] : []),
        ...candidatos.filter(c => extras.includes(c.id)).map(c => c.name),
    ]
    const n = Math.max(quienes.length, 1)
    const precio = Number(newReservation.price) || 0
    const pagado = (newReservation.payment_methods || []).reduce((sum, m) => sum + (Number(m.value) || 0), 0)
    const tourName = tour?.title_es || tour?.title || createReservationTourId
    const diaCorto = `${reservationDay.slice(8, 10)}/${reservationDay.slice(5, 7)}`

    useEffect(() => {
        if (!newReservation.passenger_id) {
            setJoinOptions([])
            setJoinChoice('none')
            return
        }
        let cancelled = false
        SellerService.listJoinOptions(newReservation.passenger_id, reservationDay)
            .then(response => {
                if (cancelled) return
                const options: CommonTypes.JoinOption[] = response.data
                setJoinOptions(options)
                // La primera es la más cercana a la fecha: casi siempre el viaje actual.
                setJoinChoice(options.length > 0 ? joinKey(options[0]) : 'none')
            })
            .catch(error => {
                console.log(error)
                if (!cancelled) { setJoinOptions([]); setJoinChoice('none') }
            })
        return () => { cancelled = true }
    }, [newReservation.passenger_id, reservationDay])

    function onSelectPayment(newMethod: string) {
        const new_data = newReservation.payment_methods ? [...newReservation.payment_methods, {name: newMethod}] : [{name: newMethod}]
        setNewReservation({...newReservation, payment_methods: new_data})
    }
    useEffect(() => {
        async function retrieveList() {
            setIsLoading(true)
            try {
                const response = await SellerService.listPassengers()
                setPassengers(response.data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        retrieveList()
    }, [])
    async function handleSubmit () {
        if (!newReservation.passenger_id) return
        setIsLoading(true)
        const ids = [newReservation.passenger_id, ...extras.filter(id => candidatos.some(c => c.id === id))]
        const creados: string[] = []
        let group_id = joinChoice.startsWith('g:') ? joinChoice.slice(2) : undefined
        try {
            for (let i = 0; i < ids.length; i++) {
                const data: CommonTypes.SubmitReservation = {
                    ...newReservation,
                    passenger_id: ids[i],
                    reservation_date: format(newReservation.reservation_date, 'yyyy-MM-dd')
                }
                // Los pagos se cargan como lo que pagaron entre todos. En un privado se manda el total y
                // el backend reparte precio y pagos del vehículo; en un compartido cada uno paga el precio
                // por persona y acá se le asigna su parte de lo pagado.
                if (!privado && ids.length > 1 && newReservation.payment_methods) {
                    data.payment_methods = newReservation.payment_methods
                        .map(m => ({...m, value: repartir(Number(m.value) || 0, ids.length)[i]}))
                        .filter(m => m.value > 0)
                }
                if (group_id) data.group_id = group_id
                else if (joinChoice.startsWith('r:')) data.join_reservation_id = joinChoice.slice(2)
                const response = await SellerService.createReservation(data)
                // Juntar con una reserva suelta crea el grupo: los siguientes van a ese mismo grupo.
                group_id = group_id || response.data?.reservation?.group_id || undefined
                creados.push(passengers.find(p => p.id === ids[i])?.name || ids[i])
            }
            window.location.reload()
        } catch (error: any) {
            console.log(error)
            const motivo = typeof error?.response?.data === 'string' ? error.response.data : 'Ocurrió un error.'
            alert(creados.length > 0
                ? `${motivo}\n\nAlcanzaron a crearse las reservas de: ${creados.join(', ')}. Revisa antes de volver a intentar.`
                : motivo)
            if (creados.length > 0) window.location.reload()
        } finally {
            setIsLoading(false)
        }
    }

    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value
        const name = e.target.name
        setNewReservation({...newReservation, [name]: value})
    }

    function onChangePayment(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value
        const name = e.target.name
        const oldItem = newReservation.payment_methods.find((item: CommonTypes.PaymentMethods) => item.name === name)
        if (!oldItem) return
        const index = newReservation.payment_methods.indexOf(oldItem)
        const filteredList = [...newReservation.payment_methods.filter((item: CommonTypes.PaymentMethods) => item.name !== name)]
        const newData = {...oldItem, value}
        setNewReservation({...newReservation, payment_methods: insert(filteredList, index, newData)})
    }

    function deletePayment(name: string) {
        const newList = newReservation.payment_methods.filter((item: CommonTypes.PaymentMethods) => item.name !== name)
        setNewReservation({...newReservation, payment_methods: newList})
    } 

    return (
        <Main>
            <Modal>
                <div className="close" title="close"
                onClick={() => setCreateReservationTourId('')}
                >
                    <AiOutlineCloseCircle/>
                </div>
                <h3>Create a new reservation</h3>
                {
                    passengers.length > 0 &&
                    <InputDropdown
                    style={{fontSize: '14px'}}
                    options={passengers}
                    keys={['email', 'id', 'name']}
                    selector="id"
                    onChange={(newValue: string) => setNewReservation((prevData: CommonTypes.CreateReservation) => ({...prevData, passenger_id: newValue}))}
                    label="Passenger ID"
                    optionDefaultHeight={80}
                    />
                }
                <div className="calendar">
                    <Calendar
                    selectedDay={newReservation.reservation_date}
                    datesStyle={{left: '-20%'}}
                    setSelectedDay={(newDate: Date) => setNewReservation((prevData: CommonTypes.CreateReservation) => ({...prevData, reservation_date: newDate}))}
                    />
                    <span>
                        {format(newReservation.reservation_date, 'yyyy-MM-dd')}
                    </span>
                </div>
                {
                    opciones.length > 0 &&
                    <fieldset className="join-options">
                        <legend>1. ¿Este tour es parte de un grupo que {pasajero?.name || 'este pasajero'} ya tiene?</legend>
                        <p className="help">Si es el mismo grupo, queda todo junto y los pagos se ven en una sola cuenta.</p>
                        {
                            opciones.map(option => (
                                <label key={joinKey(option)}>
                                    <input
                                        type="radio"
                                        name="join"
                                        checked={joinChoice === joinKey(option)}
                                        onChange={() => setJoinChoice(joinKey(option))}
                                    />
                                    <span>Sí, al grupo de: {joinLabel(option)}</span>
                                </label>
                            ))
                        }
                        <label>
                            <input
                                type="radio"
                                name="join"
                                checked={joinChoice === 'none'}
                                onChange={() => setJoinChoice('none')}
                            />
                            <span>No, es una reserva nueva aparte</span>
                        </label>
                    </fieldset>
                }
                {
                    candidatos.length > 0 &&
                    <fieldset className="join-options">
                        <legend>2. ¿Quién más de ese grupo va a {tourName} el {diaCorto}?</legend>
                        <p className="help">Marca a los que van con {pasajero?.name || 'este pasajero'}. A cada uno se le crea su reserva.</p>
                        {
                            candidatos.map(c => (
                                <label key={c.id}>
                                    <input
                                        type="checkbox"
                                        checked={extras.includes(c.id)}
                                        onChange={e => setExtras(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))}
                                    />
                                    <span>{c.name}</span>
                                </label>
                            ))
                        }
                    </fieldset>
                }
                <GeneralInput
                value={newReservation.tickets || ''}
                type="number"
                label={n > 1 ? "Ticket Price* (por pasajero)" : "Ticket Price*"}
                style={{marginTop: '20px'}}
                placeholder="20000"
                image={dollar}
                name="tickets"
                onChange={onChange}
                />
                <GeneralInput
                value={newReservation.price || ''}
                type="number"
                label={privado ? "Precio del privado (vehículo completo)*" : n > 1 ? "Sale Price* (por pasajero)" : "Sale Price*"}
                style={{marginTop: '20px'}}
                placeholder="30000"
                name="price"
                image={dollar}
                onChange={onChange}
                />
                {
                    newReservation?.payment_methods?.map((method: CommonTypes.PaymentMethods, idx: number) => {
                        return (
                            <div className="payment_method" key={idx}>
                                <button
                                onClick={() => deletePayment(method.name)}
                                >
                                    <FaTrash/>
                                </button>
                                <GeneralInput
                                value={method.value || ''}
                                name={method.name}
                                type="number"
                                placeholder="Amount"
                                label={method.name}
                                image={dollar}
                                onChange={onChangePayment}
                                />
                            </div>
                        )
                    })
                }
                {
                    n > 1 &&
                    <p className="help" style={{marginTop: '12px'}}>Pagos: lo que pagaron entre los {n}. Se divide en partes iguales.</p>
                }
                <Dropdown
                style={{marginTop: '20px'}}
                options={['Efectivo', 'Tarjeta Santander', 'Tarjeta BCI', 'Trasnferencia Santander', 'Transferencia BCI', 'Wise David', 'Link de Pago', 'Deuda'].filter(str => !newReservation?.payment_methods?.map((item: CommonTypes.PaymentMethods) => item.name).includes(str))}
                title="Payment methods"
                onSelect={onSelectPayment}
                />
                <GeneralInput
                value={newReservation.seller_remark || ''}
                label="Remark for this tour (optional)"
                style={{marginTop: '20px'}}
                placeholder="Only for this tour, e.g. vegetarian lunch"
                name="seller_remark"
                onChange={onChange}
                />
                {
                    pasajero &&
                    <dl className="resumen">
                        <dt>Resumen</dt>
                        <div><span>Tour:</span> {tourName} · {diaCorto}</div>
                        <div><span>Pasajeros ({quienes.length}):</span> {quienes.join(', ')}</div>
                        <div><span>Grupo:</span> {grupoElegido ? `se suma al grupo de ${joinLabel(grupoElegido)}` : 'reserva nueva, sin grupo'}</div>
                        {
                            precio > 0 &&
                            <div><span>Precio:</span> {
                                privado
                                    ? (n > 1 ? `vehículo ${formatCLP(precio)} → ${formatCLP(repartir(precio, n)[0])} por pasajero` : `vehículo completo ${formatCLP(precio)}`)
                                    : (n > 1 ? `${formatCLP(precio)} por pasajero → total ${formatCLP(precio * n)}` : formatCLP(precio))
                            }</div>
                        }
                        {
                            pagado > 0 &&
                            <div><span>Pagado ahora:</span> {formatCLP(pagado)}{n > 1 ? ` entre los ${n}` : ''}</div>
                        }
                    </dl>
                }
                <button
                onClick={handleSubmit}
                className="submit">{quienes.length > 1 ? `Crear ${quienes.length} reservas` : 'Create new reservation'}</button>
            </Modal>
        </Main>
    )
}

function joinKey(option: CommonTypes.JoinOption) {
    return option.type === 'group' ? `g:${option.group_id}` : `r:${option.reservation_id}`
}

// "Juan, Carlos · Valle 12/10, Cejar 13/10": quiénes y qué tours, para reconocer el grupo de un vistazo.
function joinLabel(option: CommonTypes.JoinOption) {
    const tours = option.tours.map(t => `${t.tour_id} ${t.date.slice(8, 10)}/${t.date.slice(5, 7)}`).join(', ')
    return `${option.passengers.join(', ')} · ${tours}`
}

const Main = styled.div`
z-index: 100;
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
background-color: ${props => props.theme.blackTransparent};
`

const showUpModal = keyframes`
    from {
        top: 100%;
    }
    to {
        top: 50%;
    }
`

const Modal = styled.div`
width: 85%;
overflow-y: unset;
border-radius: 10px;
max-width: 500px;
position: absolute;
top: 0;
left: 50%;
max-height: 80vh;
overflow: hidden;
overflow-y: unset;
transform: translate(-50%, -50%);
background-color: ${props => props.theme.white};
box-shadow: 0 10px 20px ${props => props.theme.blackTransparent};
padding: 20px;
animation: ${showUpModal} .3s ease forwards;
h3 {
    margin-bottom: 20px;
}
h3.payment-title {
    font-weight: 500;
    margin-bottom: 10px;
}
.payment_method {
    margin: 10px 0;
    position: relative;
    button {
        position: absolute;
        right: 0;
        top: 0;
        height: 25px;
        width: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        background-color: rgba(0,0,0,0);
        border-radius: 50%;
        cursor: pointer;
        border: 1px solid ${props => props.theme.dark};
    }
    h4 {
        font-weight: 400;
        margin-bottom: 5px;
    }
    label {
        text-transform: capitalize;
    }
}
.close {
    position: absolute;
    right: 20px;
    top: 20px;
    font-size: 22px;
    cursor: pointer;
}
> .calendar {
    display: flex;
    gap: 15px;
    margin-top: 20px;
    align-items: center;
}
.join-options {
    margin-top: 16px;
    .help {
        margin: 0 0 6px;
        font-size: 12px;
        color: #475569;
    }
    padding: 10px 12px;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    background-color: #eff6ff;
    max-height: 170px;
    overflow-y: auto;
    legend {
        padding: 0 4px;
        font-size: 14px;
        font-weight: 600;
    }
    label {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 4px 0;
        font-size: 13px;
        line-height: 1.35;
        cursor: pointer;
    }
    input {
        margin-top: 2px;
        flex-shrink: 0;
    }
}

.help {
    font-size: 12px;
    color: #475569;
}
.resumen {
    margin: 20px 0 0;
    padding: 10px 12px;
    border-radius: 8px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    font-size: 14px;
    line-height: 1.5;
    dt {
        font-weight: 600;
        margin-bottom: 4px;
    }
    span {
        font-weight: 600;
    }
}

> button.submit {
    margin-top: 20px;
    display: inline-block;
    padding: 0 10px;
    height: 2.6em;
    line-height: 2.5em;
    overflow: hidden;
    font-size: 17px;
    z-index: 1;
    background-color: ${props => props.theme.white};
    color: ${props => props.theme.yellowDark};
    border: 2px solid ${props => props.theme.yellowDark};
    border-radius: 6px;
    position: relative;
    cursor: pointer;
}

> button::before {
    position: absolute;
    content: "";
    background: ${props => props.theme.yellowDark};
    width: calc(100% + 100px);
    height: 200px;
    z-index: -1;
    border-radius: 50%;
}

> button.submit:hover {
    color: ${props => props.theme.white};
}

> button.submit:before {
    top: 100%;
    left: 100%;
    transition: .3s all;
}

> button.submit:hover::before {
    top: -30px;
    left: -30%;
}
`